import type { RcFile } from 'antd/lib/upload';
import { useFormik } from 'formik';
import debounce from 'lodash.debounce';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import * as Yup from 'yup';

import { bookApi } from '~/api/book/bookApi';
import { AgeRatingEnum } from '~/api/book/bookApiTypes';
import { CommentAccessStatusEnum, CommentSettingsNamesEnum } from '~/api/comment/commentApiTypes';
import { Button } from '~/atomic/atom/Button';
import { TextArea } from '~/atomic/atom/Input';
import { PostLink } from '~/atomic/atom/links/dynamicLinks';
import { Tag } from '~/atomic/atom/Tag';
import { Checkbox } from '~/atomic/molecula/Checkbox';
import { Field, SCField } from '~/atomic/molecula/Field';
import { FileUpload, Size } from '~/atomic/molecula/FileUpload';
import { FileVideoUpload } from '~/atomic/molecula/FileVideoUpload';
import { Select } from '~/atomic/molecula/Select';
import { descriptionInputsSelector } from '~/atomic/page/book-edit/DescriptionInputs/descriptionInputs.selector';
import {
  TextBoldStyle, TextRegStyle, TextSmallStyle,
} from '~/atomic/Typography';
import {
  removeBookCover, removeBookVideoCover, updateBookProperties,
} from '~/feature/book/book.data';
import {
  bookCoverUrlSelector, bookSelector, bookVideoCoverUrlSelector,
} from '~/feature/book/book.selector';
import { bookActions } from '~/feature/book/book.slice';
import { useAddHandlersOnChangeContent } from '~/feature/book-edit/useAddHandlersOnChangeContent';
import {
  cycleSelector,
  cyclesForSelectSelector, getCyclesMe,
} from '~/feature/cycle/cycleSlice';
import { genres } from '~/feature/genre/genres';
import { refreshBookCash } from '~/feature/refreshCash/refreshBookCash';
import { searchTag } from '~/feature/tag/tag.data';
import { tagSelector } from '~/feature/tag/tag.selector';
import { lessThan } from '~/lib/mediaQuery';
import { useAppDispatch, useAppSelector } from '~/store';

import { descriptionInputsActions } from './descriptionInputs.slice';

const MIN_SIZE_BOOK_COVER = {
  width: 300,
  height: 200,
};

const MAX_BOOK_ANNOTATION_SIZE = 2000;
const MAX_BOOK_NOTE_SIZE = 2000;

export const disabledGenresForSelect = [
  'fantastika',
  'fentezi',
  'lyubovnyi-roman',
  'erotika',
  'detektiv',
  'raznoe',
];

export const DescriptionInputs = () => {
  const dispatch = useAppDispatch();
  const {
    currentBook,
    currentBookProperties,
  } = useAppSelector(bookSelector);

  const bookCover = useAppSelector(bookCoverUrlSelector);
  const videoCover = useAppSelector(bookVideoCoverUrlSelector);
  const cyclesForSelect = useAppSelector(cyclesForSelectSelector);
  const { isCyclesMeLoading } = useAppSelector(cycleSelector);
  const { isDirtyForm } = useAppSelector(descriptionInputsSelector);
  const [isLoading, setLoading] = useState(false);

  const genresForSelect = genres.map((genre) => {
    if (disabledGenresForSelect.includes(genre.slug)) {
      return {
        value: genre.slug,
        label: genre.name,
        disabled: true,
      };
    }

    return {
      value: genre.slug,
      label: genre.name,
    };
  });

  const isFunfikIncludes = (array: string[]) => {
    return array.some((genre) => {
      return genre.includes('fanfik');
    });
  };

  const { tags } = useAppSelector(tagSelector);
  const inputsForm = useFormik({
    initialValues: {
      genres: currentBookProperties?.genres
        ?.map((genre) => genre.slug) ?? [],
      annotation: currentBookProperties?.annotation ?? '',
      note: currentBookProperties?.note ?? '',
      ageRating: currentBookProperties?.ageRating ?? '' as AgeRatingEnum,
      tags: currentBookProperties?.tags
        ?.map((tag) => tag.name) ?? [],
      cycleId: currentBookProperties?.cycleId ?? currentBook?.cycle?.id,
      isFoulLanguage: currentBookProperties?.isFoulLanguage ?? false,
      commentAccessStatus: currentBookProperties
        ?.commentAccessStatus ?? CommentAccessStatusEnum.Enabled,
      redLine: currentBookProperties?.redLine ?? true,
      download: currentBookProperties?.download ?? true,
      containsEroticScenes: currentBookProperties?.containsEroticScenes ?? false,
    },
    validateOnBlur: false,
    validationSchema: Yup.object({
      genres: Yup
        .array<string>()
        .required()
        .test('isFanfic', 'ФанФик не может быть платным', (value) => {
          if (currentBook?.free) return true;
          return !isFunfikIncludes(value);
        })
        .max(3, 'Максимальное количество жанров 3'),
      tags: Yup
        .array<string>()
        .max(14, 'Превышено максимальное количество тегов')
        .of(
          Yup.string()
            .min(2, 'Минимальная длина тега - 2 символа')
            .max(40, 'Максимальная длина тега - 40 символа')
            .test('check special symbols', 'Неверных формат тега', (value) => {
              return !value.match(/[^a-z^а-яё^\d^\s^-]/gi);
            }),
        )
        .required(),
      annotation: Yup
        .string()
        .required()
        .min(50, 'Минимальная длина 50 символов')
        .max(MAX_BOOK_ANNOTATION_SIZE, `Максимальная длина ${MAX_BOOK_ANNOTATION_SIZE} символов`),
      note: Yup
        .string()
        .max(MAX_BOOK_NOTE_SIZE, `Максимальная длина ${MAX_BOOK_NOTE_SIZE} символов`),
      ageRating: Yup
        .string()
        .required()
      ,
    }),
    onSubmit: (values, formikhelpers) => {
      setLoading(true);

      dispatch(updateBookProperties({
        ...values,
        id: currentBook?.id,
        bookSlug: currentBook.slug,
        cycle: { id: String(values.cycleId) },
      }))
        .then((res) => {
          dispatch(descriptionInputsActions.changeIsDirtyForm(false));

          setLoading(false);
          if (updateBookProperties.rejected.match(res)) {
            if (res.payload.errors?.ageRating?.[0]) {
              formikhelpers.setFieldError('ageRating', res.payload.errors?.ageRating?.[0]);
            }
            if (res.payload.errors?.note?.[0]) {
              formikhelpers.setFieldError('note', res.payload.errors?.note?.[0]);
            }
            if (res.payload.errors?.tags?.[0]) {
              formikhelpers.setFieldError('tags', res.payload.errors?.tags?.[0]);
            }
            if (res.payload.errors?.genres?.[0]) {
              formikhelpers.setFieldError('genres', res.payload.errors?.genres?.[0]);
            }
            if (res.payload.errors?.annotation?.[0]) {
              formikhelpers.setFieldError('annotation', res.payload.errors?.annotation?.[0]);
            }
            if (res.payload.errors?.commentAccessStatus?.[0]) {
              formikhelpers.setFieldError('commentAccessStatus', res.payload.errors?.commentAccessStatus?.[0]);
            }
            if (res.payload.errors?.cycleId?.[0]) {
              formikhelpers.setFieldError('cycleId', res.payload.errors?.cycleId?.[0]);
            }
          }
        });
    },
  });

  useEffect(() => {
    dispatch(descriptionInputsActions.changeIsDirtyForm(inputsForm.dirty));
  }, [inputsForm.dirty]);

  useEffect(() => {
    inputsForm.setValues({
      genres: currentBookProperties?.genres
        ?.map((genre) => genre.slug) ?? [],
      annotation: currentBookProperties?.annotation ?? '',
      note: currentBookProperties?.note ?? '',
      ageRating: currentBookProperties?.ageRating ?? '' as AgeRatingEnum,
      tags: currentBookProperties?.tags
        ?.map((tag) => tag.name) ?? [],
      cycleId: currentBookProperties?.cycleId ?? currentBook?.cycle?.id,
      isFoulLanguage: currentBookProperties?.isFoulLanguage ?? false,
      commentAccessStatus: currentBookProperties
        ?.commentAccessStatus ?? CommentAccessStatusEnum.Enabled,
      redLine: currentBookProperties?.redLine ?? true,
      download: currentBookProperties?.download ?? true,
      containsEroticScenes: currentBookProperties?.containsEroticScenes ?? false,
    });
    dispatch(descriptionInputsActions.changeIsDirtyForm(false));
  }, [currentBookProperties, currentBook.id, currentBook?.cycle?.id]);

  useAddHandlersOnChangeContent(isDirtyForm);

  const searchTags = debounce(
    (name: string) => {
      if (name.length > 0) {
        dispatch(searchTag({ name }));
      }
    },
    300,
  );

  useEffect(() => {
    if (inputsForm.isSubmitting && !inputsForm.isValid && Object.keys(inputsForm.errors).length > 0) {
      const showError = async () => {
        const { message } = await import('~/atomic/atom/message');
        message.error('Описание не сохранено. Заполните все необходимые поля');
      };

      showError();
    }
  }, [inputsForm.isSubmitting, inputsForm.isValid]);

  useEffect(() => {
    dispatch(getCyclesMe());
  }, []);

  const uploadBookCoverHandler = (file: RcFile, fileSize: Size) => {
    const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
    const isLt8M = file.size / 8 / 1024 / 1024 < 1;

    const isSufficientWidth = fileSize.width >= MIN_SIZE_BOOK_COVER.width;
    const isSufficientHeight = fileSize.height >= MIN_SIZE_BOOK_COVER.height;

    if (!isJpgOrPng) {
      import('~/atomic/atom/message')
        .then(({ message }) => {
          message.error('Вы можете использовать только JPG/PNG форматы');
        });
      return false;
    }

    if (!isLt8M) {
      import('~/atomic/atom/message')
        .then(({ message }) => {
          message.error('Изображение должно быть меньше 8 мб');
        });
      return false;
    }

    if (!isSufficientWidth) {
      import('~/atomic/atom/message')
        .then(({ message }) => {
          message.error(
            `Ширина изображения должна быть больше, либо равна ${MIN_SIZE_BOOK_COVER.width} пикселей.`,
          );
        });
      return false;
    }

    if (!isSufficientHeight) {
      import('~/atomic/atom/message')
        .then(({ message }) => {
          message.error(
            `Высота изображения должна быть больше, либо равна ${MIN_SIZE_BOOK_COVER.height} пикселей.`,
          );
        });
      return false;
    }

    return true;
  };

  const uploadBookVideoCoverHandler = (file: RcFile) => {
    const isMp4 = file.type === 'video/mp4';
    const isWebm = file.type === 'video/webm';
    const isLt15M = file.size / 15 / 1024 / 1024 < 1;

    if (!isMp4 && !isWebm) {
      import('~/atomic/atom/message')
        .then(({ message }) => {
          message.error('Вы можете использовать только MP4 и WEBM форматы');
        });

      return false;
    }

    if (!isLt15M) {
      import('~/atomic/atom/message').then(({ message }) => {
        message.error('Размер видео не должен превышать 15 MB');
      });
      return false;
    }

    return true;
  };

  return (
    <SCDescriptionInputs>
      <SCFileUploadWrapper>
        <SCImageUploader
          url={bookCover}
          action={bookApi.uploadCoverUrl(currentBook?.id)}
          name="image"
          onRemove={() => {
            return dispatch(removeBookCover({
              bookId: currentBook?.id,
              bookSlug: currentBook.slug,
            }));
          }}
          beforeUpload={uploadBookCoverHandler}
          afterUpload={(url) => {
            dispatch(bookActions.changeCurrentBookCoverUrl(url));
            refreshBookCash(currentBook.slug);
          }}
          previewText="Загрузить обложку (jpg, png)"
        />
        <>
          <SCFileVideoUpload
            url={videoCover}
            action={bookApi.uploadVideoCoverUrl(currentBook?.id)}
            name="video"
            onRemove={() => {
              return dispatch(removeBookVideoCover({
                bookId: currentBook?.id,
                bookSlug: currentBook.slug,
              }));
            }}
            beforeUpload={uploadBookVideoCoverHandler}
            afterUpload={(url) => {
              dispatch(bookActions.changeCurrentBookVideoCoverUrl(url));
              refreshBookCash(currentBook.slug);
            }}
            previewText="Загрузить видео обложку"
          />
          <SCText>
            Это анимированная с помощью ИИ обложка для отображения на странице книги вместо статичной картинки. Формат mp4, webm, пропорции 9х16,  вес до 15мб.
            <br />
            <PostLink slugPost="video-oblozhkibannery-dlya-vashikh-knig-7308">
              <SCLink target="_blank">
                Подробнее
              </SCLink>
            </PostLink>
          </SCText>
        </>
      </SCFileUploadWrapper>

      <SCForm onSubmit={inputsForm.handleSubmit}>
        <Field
          label="Жанры*"
          tip="до 3 жанров"
          error={inputsForm.touched.genres && inputsForm.errors.genres as string}
        >
          <SCGenreSelectWrapper>
            {currentBookProperties.canAuthorEditGenres === false && (
              <SCGenreSelectDisabledText>
                Жанры книги уточнены модератором
              </SCGenreSelectDisabledText>
            )}
            <SCMultiplySelect
              mode="tags"
              maxTagCount={3}
              onBlur={inputsForm.handleBlur}
              onChange={(value) => {
                inputsForm.setFieldValue('genres', value);
              }}
              value={inputsForm.values.genres}
              showArrow
              disabled={currentBookProperties.canAuthorEditGenres === false}
              tagRender={(props) => {
                return (
                  <SCTag
                    {...props}
                    closable
                  >
                    {props.label}
                  </SCTag>
                );
              }}
              placeholder="Выберите жанр произведения"
              optionFilterProp="label"
              options={
                inputsForm.values.genres.length > 2
                  ? genres
                    .filter((genre) => inputsForm.values.genres.includes((genre.slug)))
                    .map((genre) => ({ value: genre.slug, label: genre.name }))
                  : genresForSelect
              }
            />
          </SCGenreSelectWrapper>
        </Field>
        <Field
          label="Аннотация*"
          tip={`до ${MAX_BOOK_ANNOTATION_SIZE} символов`}
          error={inputsForm.touched.annotation && inputsForm.errors.annotation as string}
        >
          <SCTextArea
            name="annotation"
            value={inputsForm.values.annotation}
            onChange={inputsForm.handleChange}
            onBlur={inputsForm.handleBlur}
            // eslint-disable-next-line max-len
            placeholder="Кратко опишите сюжет, место действия и главных героев произведения. Старайтесь избегать подробностей, которые могут раскрыть читателю развязку истории."
            rows={5}
          />
        </Field>

        <Field
          label="Примечания автора"
          tip={`до ${MAX_BOOK_NOTE_SIZE} символов`}
          error={inputsForm.touched.note && inputsForm.errors.note as string}
        >
          <SCTextArea
            name="note"
            value={inputsForm.values.note}
            onChange={inputsForm.handleChange}
            onBlur={inputsForm.handleBlur}
            // eslint-disable-next-line max-len
            placeholder="Оставьте заметки к произведению: например, как часто планируется выпуск обновлений"
            rows={5}
          />
        </Field>

        <Field
          label="Возрастной рейтинг*"
          error={inputsForm.touched.ageRating && inputsForm.errors.ageRating as string}
        >
          <SCSelect
            options={
              inputsForm.values.isFoulLanguage || inputsForm.values.containsEroticScenes
                ? [{ value: AgeRatingEnum['18+'], label: AgeRatingEnum['18+'] }]
                : Object
                  .values(AgeRatingEnum)
                  .map((value) => ({ value, label: value }))
            }
            value={inputsForm.values.ageRating}
            onChange={(value) => {
              inputsForm.setFieldValue('ageRating', value);
            }}
            onBlur={inputsForm.handleBlur}
          />
        </Field>

        <SCContainsEroticScenesCheckbox
          name="isFoulLanguage"
          checked={inputsForm.values.isFoulLanguage}
          onChange={(e) => {
            inputsForm.setFieldValue('isFoulLanguage', e.target.checked);
            if (e.target.checked) inputsForm.setFieldValue('ageRating', AgeRatingEnum['18+']);
          }}
          title="Содержит нецензурную брань"
        />
        <SCContainsEroticScenesCheckbox
          name="containsEroticScenes"
          checked={inputsForm.values.containsEroticScenes}
          onChange={(e) => {
            inputsForm.setFieldValue('containsEroticScenes', e.target.checked);
            if (e.target.checked) inputsForm.setFieldValue('ageRating', AgeRatingEnum['18+']);
          }}
          title="Содержит эротические сцены"
        />
        <Field
          label="Расскажите, о чем ваше произведение"
          error={inputsForm.touched.tags && inputsForm.errors.tags as string}
          description={`
            Перечислите ключевые слова, которые помогут читателю принять решение начать читать
          `}
        >
          <SCMultiplySelect
            filterOption
            mode="tags"
            placeholder="Добавьте тег..."
            onSearch={searchTags}
            value={inputsForm.values.tags}
            onChange={(value) => {
              inputsForm.setFieldValue('tags', value);
            }}
            onBlur={inputsForm.handleBlur}
            options={tags.map((tag) => {
              return ({ value: tag.name, title: tag.name });
            })}
          />
        </Field>

        <Field
          label="Настройки комментирования"
          error={
            inputsForm.touched.commentAccessStatus
            && inputsForm.errors.commentAccessStatus as string
          }
        >
          <SCSelect
            options={Object
              .keys(CommentAccessStatusEnum)
              .map((value) => ({
                value: CommentAccessStatusEnum[value],
                label: CommentSettingsNamesEnum[value],
              }))}
            value={inputsForm.values.commentAccessStatus}
            onChange={(value) => {
              inputsForm.setFieldValue('commentAccessStatus', value);
            }}
            onBlur={inputsForm.handleBlur}
          />
        </Field>
        {inputsForm.values.commentAccessStatus === CommentAccessStatusEnum.PreModeration && (
          <SCcommentAccessStatusDescription>
            Вы можете удалить некорректные высказывания до их публикации.
            Комментарии станут доступны всем читателям через 24 часа
            после добавления или после нажатия вами кнопки
            &quot;Опубликовать&quot; на странице книги.
          </SCcommentAccessStatusDescription>
        )}

        <Field
          label="Серия книг"
          error={inputsForm.touched.cycleId && inputsForm.errors.cycleId as string}
        >
          <SCSelect
            onBlur={inputsForm.handleBlur}
            onChange={(value) => {
              inputsForm.setFieldValue('cycleId', value);
            }}
            value={inputsForm.values.cycleId}
            showArrow
            loading={isCyclesMeLoading}
            placeholder="Выберите цикл произведения"
            optionFilterProp="label"
            options={cyclesForSelect}
          />
        </Field>
        <SCDownloadCheckbox
          name="redLine"
          checked={inputsForm.values.redLine}
          onChange={(e) => {
            inputsForm.setFieldValue('redLine', e.target.checked);
          }}
          title="Отображать все абзацы с красной строки"
        />
        <SCDownloadCheckbox
          name="download"
          checked={inputsForm.values.download}
          onChange={(e) => {
            inputsForm.setFieldValue('download', e.target.checked);
          }}
          title="Разрешить читателям скачивать книгу"
        />
        <SCButton
          type="primary"
          htmlType="submit"
          size="large"
          isLoading={isLoading}
        >
          Сохранить
        </SCButton>
      </SCForm>
    </SCDescriptionInputs>
  );
};

const SCDescriptionInputs = styled.div`
  display: flex;
  padding: 8px;
  padding-bottom: 16px;

  ${lessThan('sm')} {
    flex-direction: column;
    align-items: center;
  }
`;

const SCImageUploader = styled(FileUpload)`
  max-width: 140px !important;
  height: 190px !important;
  width: 100%;
  margin-bottom: 50px;
  
  .ant-upload-picture-card-wrapper,
  .ant-upload-list-picture-card-container,
  .ant-upload-list-item,
  .ant-upload-select {
    max-width: 140px !important;
    min-height: 150px;
    width: 100% !important;
    height: 190px !important;
  }

  img {
    max-height: 190px;
  }
`;

const SCFileVideoUpload = styled(FileVideoUpload)`
  max-width: 140px !important;
  height: 190px !important;
  width: 100%;
  margin-bottom: 10px;
  
  .ant-upload-picture-card-wrapper,
  .ant-upload-list-picture-card-container,
  .ant-upload-list-item,
  .ant-upload-select {
    max-width: 140px !important;
    min-height: 150px;
    width: 100% !important;
    height: 190px !important;
  }

  video {
    max-height: 190px;
  }
`;

const SCForm = styled.form`
  width: 100%;
  max-width: 564px;
  
  ${SCField} {
    margin-bottom: 15px;
  }
`;

const SCDownloadCheckbox = styled(Checkbox)`
  margin-left: 0 !important;
  margin-bottom: 20px;
  width: 100%;

  ${lessThan('xs')} {
    margin-bottom: 15px;
  }

  .ant-checkbox-inner {
    width: 20px;
    height: 20px;
  }

  .ant-checkbox-checked .ant-checkbox-inner:after {
    left: 25%;
    width: 7px;
    height: 11px;
    top: 45%;
  }
`;

const SCContainsEroticScenesCheckbox = styled(Checkbox)`
  margin-left: 0 !important;
  margin-bottom: 20px;
  width: 100%;

  ${lessThan('xs')} {
    margin-bottom: 15px;
  }

  .ant-checkbox-inner {
    width: 20px;
    height: 20px;
  }

  .ant-checkbox-checked .ant-checkbox-inner:after {
    left: 25%;
    width: 7px;
    height: 11px;
    top: 45%;
  }
`;

const SCCheckboxText = styled.span`
  ${TextRegStyle};
  color: var(--black-color);
  margin-left: 4px;
`;

const SCButton = styled(Button)`
  ${TextBoldStyle};
  margin: 0 auto;
  width: 188px;
  color: #fff;
`;

const SCMultiplySelect = styled(Select)`
  width: 100%;
  min-height: 44px;

  .ant-select-selector {
    min-height: 44px !important;
  }
`;

const SCSelect = styled(Select)`
  width: 100%;
  min-height: 44px;
  
  .ant-select-selector {
    min-height: 44px !important;
  }
  .ant-select-selection-item,
  .ant-select-selection-item {
    line-height: 44px !important;
  }
`;

const SCTextArea = styled(TextArea)`
  font-size: 14px;
  line-height: 20px;
  color: var(--gray-color);
  
  &:focus, &:active  {
    font-size: 14px;
    box-shadow: none;
  }
`;

const SCcommentAccessStatusDescription = styled.span`
  ${TextSmallStyle};
  color: var(--gray-color);
  display: flex;
  margin-bottom: 15px;
`;

const SCFileUploadWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  max-width: 140px;
  width: 100%;
  margin-right: 40px;

  ${lessThan('sm')} {
    margin-right: 0;
  }
`;

const SCGenreSelectWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const SCGenreSelectDisabledText = styled.span`
  ${TextRegStyle};
  color: var(--green2-color);
  display: flex;
  padding-bottom: 4px;
`;

const SCTag = styled(Tag)`
  background: #f5f5f5;
  border: 1px solid #f0f0f0;
  font-family: var(--main-font);
  height: 24px;
  line-height: 22px;
  margin: 2px 8px 2px 0;
  padding: 0 7px;
  padding-inline-end: 4px;
  padding-inline-start: 8px;
`;

const SCText = styled.span`
  ${TextSmallStyle};
  font-size: 12px;
  line-height: 16px;
  color: var(--black-color);
`;

const SCLink = styled.a``;
